// src/pages/Turnos/TurnosLiquidados.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, Box, Button, Stack, TextField, Typography, useMediaQuery } from "@mui/material";
import { DataGrid, esES } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import Header from "../../components/Header";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import { getDriversByBoss } from "../../services/driverService";
import { getStoredUserType } from "../../services/authService";
import { getMisTurnos, getTurnosByDrivers, getTurnosSummary } from "../../services/turnoService";
import { useSelector } from "react-redux";

/* ===== Paletas coherentes con el resto ===== */
const PALETTE_DARK = {
  cardBg: "#1E293B",
  cardBorder: "#334155",
  text: "#F1F5F9",
  label: "#94A3B8",
  title: "#E2E8F0",
  accent: "#38BDF8",
  btn: "#3B82F6",
  btnHover: "#2563EB",
};
const PALETTE_LIGHT = {
  cardBg: "#ECECEC",
  cardBorder: "#E5E7EB",
  text: "#1E293B",
  label: "#475569",
  title: "#111827",
  accent: "#38BDF8",
  btn: "#2563EB",
  btnHover: "#1D4ED8",
};

const formatLocalDate = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const resolveLicenseLabel = (license) => {
  if (!license) return "-";
  if (typeof license === "object") {
    return license.licenseNumber || license.number || license.license || license._id || "-";
  }
  return license;
};

/* ===== Widget ===== */
function StatCard({ icon, label, value, palette, highlight = false, isMobile = false }) {
  return (
    <Box
      sx={{
        p: isMobile ? 1.75 : 2,
        borderRadius: "10px",
        backgroundColor: palette.cardBg,
        border: `1px solid ${highlight ? palette.accent : palette.cardBorder}`,
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        alignItems: "center",
        justifyContent: isMobile ? "center" : "flex-start",
        gap: isMobile ? 1 : 1.25,
        minHeight: 76,
        boxShadow:
          palette === PALETTE_LIGHT
            ? "0 6px 18px rgba(2,6,23,0.08)"
            : "0 2px 6px rgba(0,0,0,0.20)",
      }}
    >
      <Box sx={{ opacity: 0.9, display: "grid", placeItems: "center" }}>{icon}</Box>
      <Box sx={{ flex: 1, textAlign: isMobile ? "center" : "left" }}>
        <Typography
          variant="caption"
          sx={{ color: palette.label, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}
        >
          {label}
        </Typography>
        <Typography variant="h6" sx={{ color: palette.text, fontWeight: 800 }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

/* ===== Página ===== */
const TurnosLiquidados = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const palette = useMemo(
    () => (theme.palette.mode === "dark" ? PALETTE_DARK : PALETTE_LIGHT),
    [theme.palette.mode]
  );
  const isMobile = useMediaQuery("(max-width:900px)");
  const storedUser = useMemo(() => {
    try {
      const saved = localStorage.getItem("userData");
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  }, []);
  const userId = useSelector((state) => state.user.userId) || storedUser.userId || null;
  const driverId = useSelector((state) => state.user.driverId) || storedUser.driverId || null;
  const userType = useSelector((state) => state.user.type) || getStoredUserType();

  const [hojasDeTurno, setHojasDeTurno] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState({
    hojasPorLiquidar: 0,
    recaudacionPorLiquidar: 0,
    balanceEmpresa: 0,
    balanceConductor: 0,
  });
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [dateFrom, setDateFrom] = useState(() => {
    const now = new Date();
    return formatLocalDate(new Date(now.getFullYear(), now.getMonth(), 1));
  });
  const [dateTo, setDateTo] = useState(() => formatLocalDate(new Date()));

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        let closures = [];
        let drv = [];
        let targetDriverIds = [];

        if (userType === "Boss") {
          drv = await getDriversByBoss();
          const normalizedDrivers = (drv || []).map((d) => ({
            _id: d._id,
            name: d.name || d.fullName || d.username || "Sin nombre",
          }));
          setDrivers(normalizedDrivers);
          const driverIds = normalizedDrivers.map((d) => d._id);
          targetDriverIds = selectedDriver ? [selectedDriver._id] : driverIds;
          const idsToQuery = targetDriverIds.length ? targetDriverIds : driverIds;
          closures = idsToQuery.length
            ? await getTurnosByDrivers(idsToQuery, userId, {
                startDate: dateFrom || undefined,
                endDate: dateTo || undefined,
              })
            : [];
        } else {
          targetDriverIds = driverId ? [driverId] : [];
          closures = await getMisTurnos({
            driverId,
            userId,
            startDate: dateFrom || undefined,
            endDate: dateTo || undefined,
          });
        }

        const localDriverMap = new Map(
          (drv || drivers || []).map((d) => [d._id, d.name || d.fullName || d.username || "Sin nombre"])
        );

        const liquidados = (closures || []).filter((c) => c.liquidado);
        const mapped = liquidados.map((c, idx) => ({
          id: c._id || idx,
          driverId: c.driver?._id || c.driverId || "",
          driverName:
            c.driver?.name ||
            c.driverName ||
            localDriverMap.get(c.driver?._id || c.driverId) ||
            storedUser.name ||
            storedUser.fullName ||
            storedUser.username ||
            "Sin nombre",
          licencia: resolveLicenseLabel(c.license || c.licenseId),
          facturacion: (
            (() => {
              const factServicios = Number(c.facturacionServicios || 0);
              const factResRaw = Number(c.facturacionResuelta);
              if (Number.isFinite(factResRaw) && (factResRaw !== 0 || factServicios === 0)) return factResRaw;
              if (factServicios) return factServicios;
              return Number(c.facturacionTotal || 0) || Number(c.endIncome || 0) - Number(c.startIncome || 0);
            })()
          ).toFixed(2),
          tarjeta: Number(c.cardIncome || 0).toFixed(2),
          credito: Number(c.creditIncome || 0).toFixed(2),
          gasolina: Number(c.gas || 0).toFixed(2),
          kmTomar: c.startKm ?? 0,
          kmDejar: c.endKm ?? 0,
          recaudacionTomar: Number(c.startIncome || 0).toFixed(2),
          recaudacionDejar: Number(c.endIncome || 0).toFixed(2),
          fecha: c.date || c.fecha || c.createdAt || null,
        }));
        setHojasDeTurno(mapped);

        try {
          const summaryData = await getTurnosSummary({
            driverIds: targetDriverIds,
            startDate: dateFrom || undefined,
            endDate: dateTo || undefined,
            liquidado: true,
          });
          setSummary(summaryData);
        } catch (sumErr) {
          console.warn("No se pudo cargar resumen de turnos liquidados", sumErr?.message);
          setSummary({
            hojasPorLiquidar: mapped.length,
            recaudacionPorLiquidar: mapped.reduce(
              (total, h) => total + parseFloat(h.recaudacionDejar || 0),
              0
            ),
            balanceEmpresa: mapped.reduce(
              (total, h) => total + parseFloat(h.recaudacionDejar || 0) - parseFloat(h.gasolina || 0),
              0
            ),
            balanceConductor: 0,
          });
        }
      } catch (e) {
        setError(e.message || "No se pudieron cargar los turnos liquidados");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [userType, userId, driverId, dateFrom, dateTo, selectedDriver]);

  const driverOptions = useMemo(() => {
    const base = drivers.length
      ? drivers
      : Array.from(
          new Map(
            hojasDeTurno.map((h) => [h.driverId, { _id: h.driverId, name: h.driverName || "Sin nombre" }])
          ).values()
        );
    return base;
  }, [drivers, hojasDeTurno]);

  const filteredRows = useMemo(() => {
    if (!selectedDriver) return hojasDeTurno;
    return hojasDeTurno.filter((h) => h.driverId === selectedDriver._id);
  }, [hojasDeTurno, selectedDriver]);

  const formatDateValue = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? "-" : d.toLocaleDateString();
  };

  const openNativeDatePicker = (event) => {
    const input = event.target;
    if (typeof input?.showPicker === "function") {
      try {
        input.showPicker();
      } catch {
        /* Algunos navegadores piden gesto de usuario explícito */
      }
    }
  };

  // KPIs (todo liquidados)
  const totalTurnos = summary.hojasPorLiquidar ?? filteredRows.length;
  const recaudacionTotal =
    summary.recaudacionPorLiquidar != null
      ? Number(summary.recaudacionPorLiquidar || 0).toFixed(2)
      : filteredRows
          .reduce((total, h) => total + parseFloat(h.recaudacionDejar || 0), 0)
          .toFixed(2);
  const balanceEmpresaTotal =
    summary.balanceEmpresa != null
      ? Number(summary.balanceEmpresa || 0).toFixed(2)
      : filteredRows
          .reduce(
            (total, h) => total + parseFloat(h.recaudacionDejar || 0) - parseFloat(h.gasolina || 0),
            0
          )
          .toFixed(2);

  // Columnas DataGrid — botón igual al de Turnos (azul MUI)
  const columnasTurnos = [
    { field: "driverName", headerName: "Conductor", flex: 1 },
    {
      field: "fecha",
      headerName: "Fecha",
      flex: 1,
      valueFormatter: (params) => formatDateValue(params.value),
    },
    { field: "licencia", headerName: "Licencia", flex: 1 },
    { field: "facturacion", headerName: "Facturación (€)", flex: 1 },
    { field: "tarjeta", headerName: "Cobro Tarjeta (€)", flex: 1 },
    { field: "credito", headerName: "Servicio Crédito (€)", flex: 1 },
    { field: "gasolina", headerName: "Gasolina (€)", flex: 1 },
    { field: "kmTomar", headerName: "Km al Tomar", flex: 1 },
    { field: "kmDejar", headerName: "Km al Dejar", flex: 1 },
    { field: "recaudacionTomar", headerName: "Recaudación al Tomar (€)", flex: 1 },
    { field: "recaudacionDejar", headerName: "Recaudación al Dejar (€)", flex: 1 },
    {
      field: "acciones",
      headerName: "Acciones",
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          variant="contained"
          onClick={() => navigate(`/turnos/${params.row.id}`)}
          sx={{
            backgroundColor: palette.btn,
            color: "white",
            fontWeight: 700,
            textTransform: "none",
            "&:hover": { backgroundColor: palette.btnHover },
            whiteSpace: isMobile ? "normal" : "nowrap",
            px: isMobile ? 0.5 : 1.5,
            py: isMobile ? 0.35 : undefined,
            fontSize: isMobile ? "0.78rem" : "0.9rem",
            lineHeight: 1.05,
            minWidth: isMobile ? 70 : undefined,
          }}
        >
          Más detalles
        </Button>
      ),
    },
  ];

  return (
    <Box sx={{ m: "20px", display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <Header title="Turnos Liquidados" subtitle="Histórico de hojas de turno liquidadas" />
      {error && (
        <Typography color="error" variant="body2" sx={{ mb: 1 }}>
          {error}
        </Typography>
      )}

      {/* ===== Widgets arriba ===== */}
      <Box
        sx={{
          mt: 1,
          mb: 2,
          display: "flex",
          gap: 2,
          width: "100%",
          justifyContent: "space-between",
          flexWrap: "nowrap",
          overflowX: "hidden",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <StatCard
            icon={<TrendingUpIcon />}
            label="Total de turnos"
            value={totalTurnos}
            palette={palette}
            isMobile={isMobile}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <StatCard
            icon={<AttachMoneyIcon />}
            label="Recaudación total"
            value={`€${recaudacionTotal}`}
            palette={palette}
            highlight
            isMobile={isMobile}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <StatCard
            icon={<AccountBalanceIcon />}
            label="Balance empresa (total)"
            value={`€${balanceEmpresaTotal}`}
            palette={palette}
            isMobile={isMobile}
          />
        </Box>
      </Box>

      {/* ===== Filtros ===== */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "stretch", md: "center" }}
        gap={1.5}
        sx={{ mb: 1.5 }}
      >
        <Autocomplete
          size="small"
          options={driverOptions}
          getOptionLabel={(o) => o?.name || ""}
          value={selectedDriver}
          onChange={(_, v) => setSelectedDriver(v)}
          renderInput={(params) => (
            <TextField {...params} label="Filtrar por conductor" placeholder="Selecciona un conductor" />
          )}
          sx={{
            minWidth: 260,
            "& .MuiOutlinedInput-root": {
              "& .MuiOutlinedInput-input": { color: palette.text },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: palette.accent },
            },
            "& .MuiInputLabel-root": { color: palette.label },
            "& .MuiInputLabel-root.Mui-focused": { color: palette.text },
            "& .MuiOutlinedInput-input::placeholder": { color: palette.label, opacity: 0.8 },
            "& .MuiAutocomplete-popupIndicator": { color: palette.text },
          }}
          clearOnEscape
        />
        <TextField
          label="Desde"
          type="date"
          size="small"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{
            minWidth: 160,
            "& .MuiOutlinedInput-root": {
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: palette.accent },
            },
            "& .MuiInputLabel-root.Mui-focused": { color: palette.text },
          }}
          inputProps={{
            onClick: openNativeDatePicker,
          }}
        />
        <TextField
          label="Hasta"
          type="date"
          size="small"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{
            minWidth: 160,
            "& .MuiOutlinedInput-root": {
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: palette.accent },
            },
            "& .MuiInputLabel-root.Mui-focused": { color: palette.text },
          }}
          inputProps={{
            onClick: openNativeDatePicker,
          }}
        />
      </Stack>

      {/* ===== Tabla abajo — misma posición/altura estable ===== */}
      <Box
        sx={{
          flex: "1 1 auto",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflowX: "hidden",
        }}
      >
        <Box
          sx={{
            backgroundColor: palette.cardBg,
            border: `1px solid ${palette.cardBorder}`,
            borderRadius: "14px",
            p: 1.5,
            height: "100%",     // fija el alto del contenedor
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            boxShadow:
              palette === PALETTE_LIGHT
                ? "0 8px 22px rgba(2,6,23,0.08)"
                : "0 3px 10px rgba(0,0,0,0.24)",
            overflow: "hidden",
          }}
        >
          <Typography variant="h6" sx={{ color: palette.title, fontWeight: 800, mb: 1 }}>
            Hojas de Turno
          </Typography>

          <Box
            sx={{
              flex: 1,            // el DataGrid tendrá un wrapper con altura fija
              minHeight: 0,
              width: "100%",
              overflowX: "hidden",
              "& .MuiDataGrid-root": {
                border: "none",
                color: palette.text,
                backgroundColor: "transparent",
                height: "100%",   // 👈 altura estable del grid
                width: "100%",
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "transparent",
                borderBottom: `1px solid ${palette.cardBorder}`,
                color: palette.label,
                fontWeight: 700,
              },
              "& .MuiDataGrid-cell": { borderBottom: "none" },
              "& .MuiDataGrid-virtualScroller": { backgroundColor: "transparent" },
              "& .MuiDataGrid-footerContainer": {
                borderTop: `1px solid ${palette.cardBorder}`,
                backgroundColor: "transparent",
              },
            }}
          >
            <DataGrid
              loading={loading}
              rows={filteredRows}
              columns={columnasTurnos}
              columnVisibilityModel={
                isMobile
                  ? {
                      licencia: false,
                      facturacion: false,
                      tarjeta: false,
                      credito: false,
                      gasolina: false,
                      kmTomar: false,
                      kmDejar: false,
                      recaudacionTomar: false,
                    }
                  : undefined
              }
              pageSizeOptions={[5, 10, 25]}
              initialState={{ pagination: { paginationModel: { pageSize: isMobile ? 5 : 10 } } }}
              localeText={esES.components.MuiDataGrid.defaultProps.localeText}
              sx={{ height: "100%", width: "100%" }}
              // Alturas fijas para que no “salte” al paginar
              rowHeight={52}
              columnHeaderHeight={48}
            />
            {!loading && !filteredRows.length && (
              <Typography sx={{ color: palette.label, mt: 1 }}>
                No hay turnos liquidados disponibles.
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default TurnosLiquidados;
